class CocomelotShippingProgress extends HTMLElement {
  connectedCallback() {
    this.threshold = Number(this.dataset.threshold || 4900);
    document.addEventListener('cart:updated', () => this.refresh());
    this.refresh();
  }

  async refresh() {
    try {
      const response = await fetch(`${window.Shopify.routes.root}cart.js`);
      if (!response.ok) return;
      const cart = await response.json();
      const remaining = Math.max(this.threshold - cart.total_price, 0);
      const ratio = Math.min(cart.total_price / this.threshold, 1);
      const bar = this.querySelector('[data-progress]');
      const message = this.querySelector('[data-message]');
      if (bar) bar.style.width = `${ratio * 100}%`;
      if (!message) return;

      if (remaining === 0) {
        message.textContent = 'Je bestelling wordt gratis verzonden.';
      } else {
        message.textContent = `Nog ${(remaining / 100).toLocaleString('nl-NL', {
          style: 'currency',
          currency: 'EUR',
        })} tot gratis verzending.`;
      }
    } catch (_error) {
      // The cart remains fully functional when the progress enhancement fails.
    }
  }
}

if (!customElements.get('cocomelot-shipping-progress')) {
  customElements.define('cocomelot-shipping-progress', CocomelotShippingProgress);
}

const loadProductCardSecondaryImage = (card) => {
  const image = card.querySelector('[data-cc-secondary-image]');
  if (!image || image.src) return;
  image.src = image.dataset.src;
  image.srcset = image.dataset.srcset;
  delete image.dataset.src;
  delete image.dataset.srcset;
};

if (window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
  document.querySelectorAll('.cc-product-card__media').forEach((card) => {
    card.addEventListener('mouseenter', () => loadProductCardSecondaryImage(card), { once: true });
    card.addEventListener('focusin', () => loadProductCardSecondaryImage(card), { once: true });
  });
}

const initializeProductCarousel = (carousel) => {
  if (carousel.dataset.ccCarouselReady === 'true') return;

  const track = carousel.querySelector('[data-cc-carousel-track]');
  const cards = [...track?.querySelectorAll('.cc-product-card') || []];
  const previousButton = carousel.querySelector('[data-cc-carousel-previous]');
  const nextButton = carousel.querySelector('[data-cc-carousel-next]');
  const currentLabel = carousel.querySelector('[data-cc-carousel-current]');
  const totalLabel = carousel.querySelector('[data-cc-carousel-total]');
  if (!track || !cards.length || !previousButton || !nextButton) return;

  carousel.dataset.ccCarouselReady = 'true';
  if (totalLabel) totalLabel.textContent = String(cards.length);

  const currentIndex = () => {
    const trackLeft = track.getBoundingClientRect().left;
    let closestIndex = 0;
    let closestDistance = Number.POSITIVE_INFINITY;
    cards.forEach((card, index) => {
      const distance = Math.abs(card.getBoundingClientRect().left - trackLeft);
      if (distance < closestDistance) {
        closestDistance = distance;
        closestIndex = index;
      }
    });
    return closestIndex;
  };

  const update = () => {
    const index = currentIndex();
    const trackRight = track.getBoundingClientRect().right;
    let lastVisibleIndex = -1;
    cards.forEach((card, cardIndex) => {
      if (card.getBoundingClientRect().right <= trackRight + 2) lastVisibleIndex = cardIndex;
    });
    if (currentLabel) currentLabel.textContent = String(index + 1);
    previousButton.disabled = index === 0;
    nextButton.disabled = lastVisibleIndex === cards.length - 1;
  };

  const scrollToCard = (index) => {
    const target = cards[Math.max(0, Math.min(index, cards.length - 1))];
    if (!target) return;
    const left = track.scrollLeft + target.getBoundingClientRect().left - track.getBoundingClientRect().left;
    track.scrollTo({ left, behavior: 'smooth' });
  };

  previousButton.addEventListener('click', () => scrollToCard(currentIndex() - 1));
  nextButton.addEventListener('click', () => scrollToCard(currentIndex() + 1));

  let updateFrame;
  const scheduleUpdate = () => {
    window.cancelAnimationFrame(updateFrame);
    updateFrame = window.requestAnimationFrame(update);
  };
  track.addEventListener('scroll', scheduleUpdate, { passive: true });
  window.addEventListener('resize', scheduleUpdate, { passive: true });
  update();
};

document.querySelectorAll('[data-cc-product-carousel]').forEach(initializeProductCarousel);

const customizeCookieBanner = () => {
  const banner = document.querySelector('#shopify-pc__banner');
  if (!banner || banner.dataset.cocomelotStyled === 'true') return Boolean(banner);

  const title = banner.querySelector('#shopify-pc__banner__body-title');
  const body = banner.querySelector('.shopify-pc__banner__body p');
  const policyLink = body?.querySelector('a');
  const preferencesButton = banner.querySelector('#shopify-pc__banner__btn-manage-prefs span');
  const acceptButton = banner.querySelector('#shopify-pc__banner__btn-accept');
  const declineButton = banner.querySelector('#shopify-pc__banner__btn-decline');

  // Shopify bouwt de banner soms in meerdere DOM-stappen op. Wacht tot alle
  // inhoud aanwezig is, zodat de observer niet te vroeg stopt op trage pagina's.
  if (!title || !body || !policyLink || !preferencesButton || !acceptButton || !declineButton) return false;

  title.textContent = 'Jouw privacy';
  body.replaceChildren(
    document.createTextNode('We gebruiken cookies om de shop goed te laten werken en, met jouw toestemming, voor analyse en marketing. Lees meer in ons '),
    policyLink,
    document.createTextNode('.'),
  );
  preferencesButton.textContent = 'Voorkeuren';
  acceptButton.textContent = 'Accepteren';
  declineButton.textContent = 'Alleen noodzakelijk';
  banner.dataset.cocomelotStyled = 'true';
  return true;
};

if (!customizeCookieBanner()) {
  const cookieBannerObserver = new MutationObserver(() => {
    if (customizeCookieBanner()) cookieBannerObserver.disconnect();
  });
  cookieBannerObserver.observe(document.body, { childList: true, subtree: true });
}

const initializeCartDrawerLinks = () => {
  if (document.documentElement.dataset.ccCartDrawerLinksReady === 'true') return;
  document.documentElement.dataset.ccCartDrawerLinksReady = 'true';

  document.addEventListener('click', (event) => {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
      return;
    }

    if (!(event.target instanceof Element)) return;
    const link = event.target.closest('a[href]');
    if (!link || link.hasAttribute('download') || link.target === '_blank') return;

    let destination;
    try {
      destination = new URL(link.href, window.location.origin);
    } catch (_error) {
      return;
    }

    const cartPath = new URL(window.routes?.cart_url || '/cart', window.location.origin).pathname.replace(/\/$/, '');
    if (destination.origin !== window.location.origin || destination.pathname.replace(/\/$/, '') !== cartPath) return;

    const drawer = document.querySelector('cart-drawer');
    if (!drawer || typeof drawer.open !== 'function') return;

    event.preventDefault();
    drawer.open(link);
  });
};

initializeCartDrawerLinks();

const sizeOrder = new Map([
  ['EENMAAT', 10],
  ['ONESIZE', 10],
  ['OS', 10],
  ['XXS', 100],
  ['XS', 110],
  ['S', 120],
  ['SM', 125],
  ['M', 130],
  ['ML', 135],
  ['L', 140],
  ['LXL', 145],
  ['XL', 150],
  ['XXL', 160],
  ['2XL', 160],
  ['XXXL', 170],
  ['3XL', 170],
  ['4XL', 180],
  ['5XL', 190],
  ['6XL', 200],
]);

const normalizeSize = (value) => String(value || '')
  .normalize('NFKD')
  .replace(/[\u0300-\u036f]/g, '')
  .toUpperCase()
  .replace(/[^A-Z0-9]/g, '');

const sizeRank = (value) => {
  const normalized = normalizeSize(value);
  if (sizeOrder.has(normalized)) return sizeOrder.get(normalized);
  const numericSize = String(value || '').trim().match(/^\d+(?:[.,]\d+)?/);
  if (numericSize) return 1_000 + Number(numericSize[0].replace(',', '.'));
  return 10_000;
};

const compareSizes = (first, second) => (
  sizeRank(first) - sizeRank(second)
  || String(first).localeCompare(String(second), 'nl', { numeric: true, sensitivity: 'base' })
);

const sortSizeOptions = (container) => {
  if (container instanceof HTMLSelectElement) {
    const options = [...container.options];
    const sorted = [...options].sort((first, second) => compareSizes(first.value, second.value));
    if (options.every((option, index) => option === sorted[index])) return;
    sorted.forEach((option) => container.append(option));
    return;
  }

  const inputs = [...container.children].filter((child) => (
    child instanceof HTMLInputElement && child.type === 'radio'
  ));
  if (inputs.length < 2) return;

  const entries = inputs.map((input, index) => ({
    input,
    index,
    label: [...container.children].find((child) => (
      child instanceof HTMLLabelElement && child.htmlFor === input.id
    )),
  }));
  const sorted = [...entries].sort((first, second) => (
    compareSizes(first.input.value, second.input.value) || first.index - second.index
  ));
  if (entries.every((entry, index) => entry === sorted[index])) return;

  const anchor = document.createComment('cocomelot-size-order');
  container.insertBefore(anchor, inputs[0]);
  sorted.forEach(({ input, label }) => {
    container.insertBefore(input, anchor);
    if (label) container.insertBefore(label, anchor);
  });
  anchor.remove();
};

const initializeSizeOptionOrder = (root = document) => {
  if (root instanceof Element && root.matches('[data-cc-size-options]')) sortSizeOptions(root);
  root.querySelectorAll?.('[data-cc-size-options]').forEach(sortSizeOptions);
};

initializeSizeOptionOrder();

const sizeOptionObserver = new MutationObserver((mutations) => {
  mutations.forEach((mutation) => {
    mutation.addedNodes.forEach((node) => {
      if (node instanceof Element) initializeSizeOptionOrder(node);
    });
  });
});
sizeOptionObserver.observe(document.querySelector('main') || document.body, { childList: true, subtree: true });
